import { z } from 'zod'
import { logRequestFailure } from '@/helpers/request-outcome'
import { api } from '@/helpers/apiClient'

import { userDtoSchema, userListDtoSchema, type UserDto, type UserListDto } from '@lib/dto/user.dto'
import type { InviteByEmailInput } from '@lib/input/user.input'

/** Platform SUPER_ADMIN user management (super admin only). */
class SuperAdminService {
  async list(): Promise<UserListDto> {
    try {
      return await api.get('/super-admins', userListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list super admins', {}, error)
      throw error
    }
  }

  async invite(input: InviteByEmailInput): Promise<UserDto> {
    try {
      return await api.post('/super-admins', input, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to invite super admin', {}, error)
      throw error
    }
  }

  async deletePending(userId: string): Promise<void> {
    try {
      await api.del(`/super-admins/${userId}`)
    } catch (error) {
      logRequestFailure('Failed to delete invited super admin', { userId }, error)
      throw error
    }
  }

  async deactivate(userId: string): Promise<UserDto> {
    try {
      return await api.post(`/super-admins/${userId}/deactivate`, {}, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to deactivate super admin', { userId }, error)
      throw error
    }
  }

  async reactivate(userId: string): Promise<UserDto> {
    try {
      return await api.post(`/super-admins/${userId}/reactivate`, {}, userDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reactivate super admin', { userId }, error)
      throw error
    }
  }

  async resendInvite(userId: string): Promise<void> {
    try {
      await api.post(`/super-admins/${userId}/resend-invite`, {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to resend super admin invite', { userId }, error)
      throw error
    }
  }
}

export const superAdminService = new SuperAdminService()
